/**
 * Geometry for the closed launcher frame in the **iframe** embed.
 *
 * The iframe is an opaque rectangle that clips anything overflowing it. When
 * the widget is closed, its content (the FAB, the unread badge that overflows
 * the FAB's top-right, the drop shadow, and — when an operator message lands —
 * the content-sized peek bubble) must fit inside that rectangle or it gets
 * cut off. The frame is anchored to the viewport's bottom corner, so growing
 * it means extending it UP and toward the OPPOSITE side.
 *
 * This computes how big the frame must be, from the measured boxes of the
 * launcher elements and the current viewport. It is pure so the geometry is
 * unit-testable; the DOM measurement + observers live in the shell effect.
 */

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

/**
 * Size (px) the closed iframe needs so every launcher box fits, measured from
 * the viewport's bottom corner outward.
 *
 * - `boxes`: viewport-relative rects of the launcher elements (FAB, peek).
 *   Zero-area boxes (e.g. an unmounted peek) are ignored.
 * - `viewport`: the iframe's own `innerWidth` / `innerHeight`.
 * - `side`: which viewport corner the frame is pinned to (the operator's launcher side).
 * - `pad`: extra margin added on the grown (top/opposite) sides for the drop
 *   shadow and the FAB's hover scale, which both extend past the layout box.
 *
 * Returns `null` when no box has area — the caller should keep the current
 * size rather than collapse the frame to `pad × pad`.
 *
 * The bottom/anchored-corner shadow is intentionally NOT padded here: the
 * loader pins the closed frame to the viewport corner, so that shadow clips at
 * the screen edge exactly like the in-DOM embeds.
 */
export function closedFrameExtent(
  boxes: Box[],
  viewport: Size,
  pad: number,
  side: "left" | "right" = "right",
): Size | null {
  let leftMost = viewport.width;
  let rightMost = 0;
  let topMost = viewport.height;
  let found = false;
  for (const b of boxes) {
    if (b.width <= 0 && b.height <= 0) continue;
    found = true;
    if (b.left < leftMost) leftMost = b.left;
    if (b.left + b.width > rightMost) rightMost = b.left + b.width;
    if (b.top < topMost) topMost = b.top;
  }
  if (!found) return null;
  // `leftMost` / `topMost` can be negative while the frame is still too small
  // for the content (the element overflows the current frame to the left/up);
  // `viewport - leftMost` then exceeds the viewport, which is correct — it's
  // the size the frame must grow to so nothing is clipped.
  // A left-anchored launcher pins the frame to the LEFT edge, so the frame grows across to the
  // right instead: its width is how far the content reaches from that edge.
  return {
    width:
      side === "left" ? Math.ceil(rightMost) + pad : Math.ceil(viewport.width - leftMost) + pad,
    height: Math.ceil(viewport.height - topMost) + pad,
  };
}
